import { useState } from 'react';
import { formatearCLP, type ModoFacturacion } from '@lexicore/shared';
import { IconoCandado, IconoCerrar } from '../../componentes/Iconos';
import { useAvisos } from '../../contextos/AvisosContext';
import { useUsuariosEmpresa, type Proyecto, type ProyectoDetalle } from '../../lib/consultas';
import { useActualizarProyecto } from '../../lib/mutaciones';

interface Props {
  proyecto: Proyecto | ProyectoDetalle;
  alCerrar: () => void;
  alGuardarExitoso?: () => void;
}

export function ModalEditarProyecto({ proyecto, alCerrar, alGuardarExitoso }: Props) {
  const avisos = useAvisos();
  const actualizarMut = useActualizarProyecto();
  const usuariosQuery = useUsuariosEmpresa();

  const [nombre, setNombre] = useState(proyecto.nombre);
  const [descripcion, setDescripcion] = useState(
    'descripcion' in proyecto ? proyecto.descripcion ?? '' : '',
  );
  const [responsableId, setResponsableId] = useState<string>(
    proyecto.responsable?.id ?? '',
  );
  const [modoFacturacion, setModoFacturacion] = useState<ModoFacturacion>(
    (proyecto.modoFacturacion as ModoFacturacion) ?? 'AL_CIERRE',
  );
  const [numeroOrdenCompra, setNumeroOrdenCompra] = useState(
    'numeroOrdenCompra' in proyecto ? proyecto.numeroOrdenCompra ?? '' : '',
  );
  const [fechaOrdenCompra, setFechaOrdenCompra] = useState(
    'fechaOrdenCompra' in proyecto && proyecto.fechaOrdenCompra
      ? proyecto.fechaOrdenCompra.slice(0, 10)
      : '',
  );
  const [notas, setNotas] = useState(
    'notas' in proyecto ? proyecto.notas ?? '' : '',
  );

  const clienteNombre =
    proyecto.licitacion?.organismo.nombre ?? proyecto.organismo?.nombre ?? 'Sin organismo';

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nombre.trim()) {
      avisos.error('El nombre del proyecto es obligatorio.');
      return;
    }

    try {
      await actualizarMut.mutateAsync({
        id: proyecto.id,
        nombre: nombre.trim(),
        descripcion: descripcion.trim() || undefined,
        responsableId: responsableId || null,
        modoFacturacion,
        numeroOrdenCompra: numeroOrdenCompra.trim() || undefined,
        fechaOrdenCompra: fechaOrdenCompra || undefined,
        notas: notas.trim() || undefined,
      });

      avisos.exito('Proyecto actualizado correctamente.');
      if (alGuardarExitoso) alGuardarExitoso();
      alCerrar();
    } catch (err) {
      avisos.error(
        err instanceof Error ? err.message : 'No se pudo actualizar el proyecto.',
      );
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && alCerrar()}
      role="dialog"
      aria-modal="true"
    >
      <div className="modal" style={{ maxWidth: 760 }}>
        <div className="modal-header">
          <div>
            <h2>Editar Proyecto</h2>
            <p style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>
              Código: <strong style={{ fontFamily: 'var(--font-mono)' }}>{proyecto.codigo}</strong>
            </p>
          </div>
          <button
            type="button"
            className="btn-icono"
            onClick={alCerrar}
            aria-label="Cerrar modal"
          >
            <IconoCerrar size={14} />
          </button>
        </div>

        <form onSubmit={guardar}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* ── Sección de Datos Contractuales Inmutables ── */}
            <div
              style={{
                background: 'var(--bg-elevated)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-md)',
                padding: '16px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 12,
                  gap: 8,
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <IconoCandado size={14} style={{ color: 'var(--accent-yellow)' }} />
                  <span
                    style={{
                      fontSize: 11.5,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      color: 'var(--text-secondary)',
                      letterSpacing: '0.02em',
                    }}
                  >
                    Condiciones Contractuales Fijas
                  </span>
                </div>
                <span
                  style={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: 20,
                    background: 'var(--accent-yellow-soft)',
                    color: 'var(--accent-yellow)',
                    border: '1px solid rgba(255, 193, 7, 0.3)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <IconoCandado size={10} /> Dato contractual fijo (Inmutable)
                </span>
              </div>

              <p style={{ fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.4 }}>
                Estos campos provienen de la licitación y contrato base adjudicado. No pueden alterarse en la edición operacional para preservar la consistencia contable y tributaria.
              </p>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: 12,
                }}
              >
                <div className="form-group">
                  <label>Cliente / Organismo</label>
                  <input
                    type="text"
                    value={clienteNombre}
                    disabled
                    style={{ background: 'var(--bg-surface)', cursor: 'not-allowed', opacity: 0.8 }}
                  />
                </div>

                <div className="form-group">
                  <label>
                    {proyecto.modalidadMonto === 'BRUTO' && proyecto.montoContratoBruto
                      ? 'Monto Contrato Total (IVA incl.)'
                      : 'Monto Contrato Neto'}
                  </label>
                  <input
                    type="text"
                    value={formatearCLP(
                      proyecto.modalidadMonto === 'BRUTO' && proyecto.montoContratoBruto
                        ? proyecto.montoContratoBruto
                        : proyecto.montoContratoNeto,
                    )}
                    disabled
                    style={{
                      fontFamily: 'var(--font-mono)',
                      background: 'var(--bg-surface)',
                      cursor: 'not-allowed',
                      opacity: 0.8,
                    }}
                  />
                  {proyecto.modalidadMonto === 'BRUTO' && proyecto.montoContratoBruto && (
                    <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 2 }}>
                      Neto: {formatearCLP(proyecto.montoContratoNeto)} · IVA 19%: {formatearCLP(proyecto.montoContratoBruto - proyecto.montoContratoNeto)}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>Fecha Inicio Contractual</label>
                  <input
                    type="text"
                    value={proyecto.fechaInicio.slice(0, 10)}
                    disabled
                    style={{ background: 'var(--bg-surface)', cursor: 'not-allowed', opacity: 0.8 }}
                  />
                </div>

                <div className="form-group">
                  <label>Fecha Término Planificada</label>
                  <input
                    type="text"
                    value={proyecto.fechaFinPlanificada ? proyecto.fechaFinPlanificada.slice(0, 10) : 'Sin plazo fijo'}
                    disabled
                    style={{ background: 'var(--bg-surface)', cursor: 'not-allowed', opacity: 0.8 }}
                  />
                </div>
              </div>
            </div>

            {/* ── Sección de Campos Operacionales Editables ── */}
            <div>
              <h3
                style={{
                  fontSize: 12.5,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  color: 'var(--text-primary)',
                  marginBottom: 14,
                  letterSpacing: '0.02em',
                }}
              >
                Datos Operativos Editables
              </h3>

              <div className="form-grid">
                <div className="form-group ancho-total">
                  <label htmlFor="editNombreProyecto">
                    Nombre del Proyecto <span style={{ color: 'var(--accent-red)' }}>*</span>
                  </label>
                  <input
                    id="editNombreProyecto"
                    type="text"
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    placeholder="Nombre descriptivo del proyecto"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="editResponsable">Responsable de Proyecto</label>
                  <select
                    id="editResponsable"
                    value={responsableId}
                    onChange={(e) => setResponsableId(e.target.value)}
                  >
                    <option value="">-- Sin responsable asignado --</option>
                    {usuariosQuery.data?.map((usuario) => (
                      <option key={usuario.id} value={usuario.id}>
                        {usuario.nombre} ({usuario.email})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="editModoFacturacion">Modo de Facturación Operativa</label>
                  <select
                    id="editModoFacturacion"
                    value={modoFacturacion}
                    onChange={(e) => setModoFacturacion(e.target.value as ModoFacturacion)}
                  >
                    <option value="AL_CIERRE">Factura Única al Cierre (AL_CIERRE)</option>
                    <option value="CONTRA_OC">Contra Orden de Compra (CONTRA_OC)</option>
                    <option value="MENSUAL">Mensual Recurrente (MENSUAL)</option>
                    <option value="POR_HITOS">Contra Hitos Facturables (POR_HITOS)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="editNumeroOC">N° Orden de Compra (OC)</label>
                  <input
                    id="editNumeroOC"
                    type="text"
                    value={numeroOrdenCompra}
                    onChange={(e) => setNumeroOrdenCompra(e.target.value)}
                    placeholder="Ej: OC-45218-2026"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="editFechaOC">Fecha de Orden de Compra</label>
                  <input
                    id="editFechaOC"
                    type="date"
                    value={fechaOrdenCompra}
                    onChange={(e) => setFechaOrdenCompra(e.target.value)}
                  />
                </div>

                <div className="form-group ancho-total">
                  <label htmlFor="editDescripcion">Descripción / Alcance del Proyecto</label>
                  <textarea
                    id="editDescripcion"
                    rows={3}
                    value={descripcion}
                    onChange={(e) => setDescripcion(e.target.value)}
                    placeholder="Detalles sobre el alcance, entregables o características del proyecto…"
                    style={{ resize: 'vertical' }}
                  />
                </div>

                <div className="form-group ancho-total">
                  <label htmlFor="editNotas">Notas Internas de Seguimiento</label>
                  <textarea
                    id="editNotas"
                    rows={2}
                    value={notas}
                    onChange={(e) => setNotas(e.target.value)}
                    placeholder="Anotaciones internas del equipo de gestión…"
                    style={{ resize: 'vertical' }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={alCerrar}
              disabled={actualizarMut.isPending}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={actualizarMut.isPending || !nombre.trim()}
            >
              {actualizarMut.isPending ? 'Guardando cambios…' : 'Guardar Proyecto'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
